import { ReactNode } from 'react'
import { Box, Skeleton, Button, Fade, Tooltip } from '@mui/material'
import {
    CoreTypography,
    IconButton,
    LeafIcon,
    useTheme
} from '@thriveglobal/thrive-web-leafkit'
import { IconName } from '@fortawesome/fontawesome-common-types'

export default function Section({
    title,
    subTitle,
    iconName,
    tooltipText,
    action,
    children,
    showLoading,
    sx
}: {
    title?: string
    subTitle?: string
    iconName?: IconName
    tooltipText?: string
    children: ReactNode
    showLoading?: boolean
    action?: { title: string; ariaLabel: string; onClick?: () => void }
    sx?: Record<string, string | number>
}) {
    const theme = useTheme()

    return (
        <Fade in>
            <Box component="section" sx={sx}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: theme.spacing(2),
                        mb: theme.spacing(subTitle ? 1 : 2)
                    }}
                >
                    <Box
                        style={{
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        {showLoading ? (
                            <Skeleton variant="text" height={50} width={150} />
                        ) : tooltipText ? (
                            <>
                                <Tooltip
                                    title={
                                        <CoreTypography variant="caption">
                                            {tooltipText}
                                        </CoreTypography>
                                    }
                                    placement="bottom-start"
                                    arrow
                                >
                                    <Box sx={{ mr: theme.spacing(0.5) }}>
                                        <IconButton
                                            size="small"
                                            variant="icon"
                                            aria-label={tooltipText}
                                        >
                                            <LeafIcon
                                                icon={iconName || 'sparkles'}
                                            />
                                        </IconButton>
                                    </Box>
                                </Tooltip>
                                <CoreTypography variant="h3" component="h2">
                                    {title}
                                </CoreTypography>
                            </>
                        ) : (
                            <CoreTypography variant="h3" component="h2">
                                {title}
                            </CoreTypography>
                        )}
                    </Box>
                    {action && (
                        <>
                            {showLoading ? (
                                <Skeleton
                                    variant="text"
                                    height={50}
                                    width={100}
                                />
                            ) : (
                                <Button
                                    onClick={action.onClick}
                                    aria-label={action.ariaLabel}
                                    color={'primary'}
                                >
                                    <CoreTypography
                                        variant="h3"
                                        sx={{
                                            fontSize: '1.8rem'
                                        }}
                                    >
                                        {action.title}
                                    </CoreTypography>
                                </Button>
                            )}
                        </>
                    )}
                </Box>
                {!showLoading && subTitle && (
                    <Box sx={{ mb: theme.spacing(2) }}>
                        <CoreTypography variant="body1">
                            {subTitle}
                        </CoreTypography>
                    </Box>
                )}

                {children}
            </Box>
        </Fade>
    )
}
